/**
 * LA FLOTA DE PCs (FLOTA-PANEL-VISTA-T1; SI de Ares #32349, contrato A #32359).
 *
 * El 21-22 oct no sabiamos en que version estaba el PC de la drogueria. La tabla "Estado global" de Desktop Admin pinta
 * UNA fila por negocio con la version del PC visto mas reciente; aqui va un renglon por PC, con su version, cuantas va
 * atras, cuando se vio y sus banderas (GET /api/v1/flota/banderas, nexo-api 593: llegan desde el escritorio 1277).
 *
 * CUANTAS VA ATRAS se cuenta por NUMERO, no por fecha: en app_versions la 1273 se publico despues de la 1274 (medido
 * 22-oct). Es el numero de versiones publicadas mayores que la del PC. Si la del PC es mas vieja que todo el historial
 * leido, se dice "mas de N" y no se inventa la cifra. Una version que no esta publicada no se cuenta: se dice.
 *
 * Nada secreto: el panel no pinta huella ni token; casa banderas y maquina por negocio + los 8 primeros de la huella
 * (huella8, la regla de A).
 *
 * Sin DOM: lo prueba scripts/check-flota-pcs.mjs.
 *
 * @module application/desktop/flotaPcs
 */

const de = (o, ...ks) => { for (const k of ks) if (o && o[k] !== undefined && o[k] !== null) return o[k]; return undefined; };
const texto = (v) => (v === undefined || v === null ? '' : String(v).trim());

/** 1.0.0.1276 -> [1,0,0,1276]; null si no es una version. */
export function partesDeVersion(v) {
  const s = texto(v).replace(/^v/i, '');
  if (!/^\d+(\.\d+)*$/.test(s)) return null;
  return s.split('.').map(Number);
}

export function compararVersiones(a, b) {
  const x = partesDeVersion(a) || [];
  const y = partesDeVersion(b) || [];
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    const d = (x[i] || 0) - (y[i] || 0);
    if (d) return d;
  }
  return 0;
}

/**
 * Cuantas versiones publicadas son mayores que la del PC.
 * @param {string} version del PC
 * @param {string[]} publicadas las del historial leido
 * @param {boolean} historialCompleto false si el historial se corto por limite (no se sabe si hay mas viejas)
 * @returns {{atras:number|null, masDe:boolean, publicada:boolean}}
 */
export function versionesAtras(version, publicadas, historialCompleto = true) {
  const lista = [...new Set((publicadas || []).map(texto).filter((v) => partesDeVersion(v)))];
  if (!partesDeVersion(version) || !lista.length) return { atras: null, masDe: false, publicada: false };
  const publicada = lista.some((v) => compararVersiones(v, version) === 0);
  const mayores = lista.filter((v) => compararVersiones(v, version) > 0).length;
  const masVieja = lista.reduce((m, v) => (compararVersiones(v, m) < 0 ? v : m), lista[0]);
  if (!publicada && compararVersiones(version, masVieja) < 0 && !historialCompleto) return { atras: lista.length, masDe: true, publicada: false };
  return { atras: mayores, masDe: false, publicada };
}

const huella8 = (fp) => texto(fp).slice(0, 8);

/**
 * Un renglon por PC agrupado por negocio. Un PC es una huella por negocio (la fila mas reciente gana); sin huella, el
 * nombre del PC. Negocios ordenados con los atrasados primero y luego por nombre; PCs por ultima vez visto.
 */
export function flotaPorNegocio({ maquinas = [], negocios = [], banderas = [], publicadas = [], historialCompleto = true }) {
  const nombre = new Map(negocios.map((b) => [texto(de(b, 'businessId', 'business_id', 'id')), texto(de(b, 'displayName', 'display_name', 'nombre')) || texto(de(b, 'businessId', 'business_id', 'id'))]));
  const bandera = new Map();
  for (const x of banderas) {
    const k = texto(de(x, 'businessId', 'business_id')) + '|' + huella8(de(x, 'huella8', 'huella'));
    const previa = bandera.get(k);
    if (!previa || texto(de(x, 'vistoAt', 'visto_at')) > texto(de(previa, 'vistoAt', 'visto_at'))) bandera.set(k, x);
  }
  const pcs = new Map();
  for (const m of maquinas) {
    const negocio = texto(de(m, 'business_id', 'businessId'));
    if (!negocio || !nombre.has(negocio)) continue;
    const fp = huella8(de(m, 'machine_fingerprint', 'machineFingerprint'));
    const pcName = texto(de(m, 'pc_name', 'pcName'));
    const k = negocio + '|' + (fp || 'pc:' + pcName);
    const visto = texto(de(m, 'last_seen', 'lastSeen'));
    const previo = pcs.get(k);
    if (previo && previo.vistoAt >= visto) continue;
    const b = fp ? bandera.get(negocio + '|' + fp) : undefined;
    const version = texto(b ? de(b, 'version') : '') || texto(de(m, 'app_version', 'appVersion'));
    pcs.set(k, {
      negocio, pcName: pcName || 'PC sin nombre', version, vistoAt: visto,
      ...versionesAtras(version, publicadas, historialCompleto),
      banderas: b ? {
        interfaz: texto(de(b, 'interfaz')), timer: de(b, 'timer') ?? null, financiero: de(b, 'financiero') ?? null,
        tokenPresente: de(b, 'tokenPresente', 'token_presente') ?? null, vistoAt: texto(de(b, 'vistoAt', 'visto_at'))
      } : null
    });
  }
  const grupos = new Map();
  for (const p of pcs.values()) {
    if (!grupos.has(p.negocio)) grupos.set(p.negocio, { businessId: p.negocio, negocio: nombre.get(p.negocio), pcs: [] });
    grupos.get(p.negocio).pcs.push(p);
  }
  const lista = [...grupos.values()].map((g) => ({
    ...g,
    pcs: g.pcs.sort((a, b) => b.vistoAt.localeCompare(a.vistoAt)),
    atrasados: g.pcs.filter((p) => p.atras > 0 || p.masDe).length
  }));
  return lista.sort((a, b) => (b.atrasados > 0) - (a.atrasados > 0) || a.negocio.localeCompare(b.negocio, 'es'));
}

/** Cuantos PCs en total, cuantos en la ultima y cuantos atrasados (los que no se saben van aparte). */
export function resumenFlota(grupos) {
  const todos = grupos.flatMap((g) => g.pcs);
  return {
    pcs: todos.length,
    alDia: todos.filter((p) => p.atras === 0).length,
    atrasados: todos.filter((p) => p.atras > 0 || p.masDe).length,
    sinSaber: todos.filter((p) => p.atras === null).length,
    conBanderas: todos.filter((p) => p.banderas).length
  };
}
